<template>
    <transition name="fade">
        <aside
            v-show="isVisible"
            :class="{ 'snackbar--modal' : snackbar.persist }"
            class="snackbar"
            :style="snackbarStyles"
        >
            <div class="snackbar__content">
                <h6 v-if="title" class="snackbar__title">
                    {{ title }}
                </h6>

                <span class="snackbar__text">
                    {{ text }}
                </span>

                <ul
                    v-if="snackbar.messages && snackbar.messages.length"
                    class="snackbar__messages"
                >
                    <li v-for="(message, index) in snackbar.messages" :key="index">
                        <button
                            v-if="message.onClick"
                            @click="message.onClick"
                        >
                            {{ message.text }}
                        </button>
                        <template v-else>
                            {{ message.text }}
                        </template>
                    </li>
                </ul>
            </div>

            <button class="snackbar__button" type="button" @click="isVisible = false">
                {{ closeText }}
            </button>
        </aside>
    </transition>
</template>

<script>
import { mapState } from 'vuex';
import listenOnRootMixin from '@/mixins/listen-on-root';

export default {
    mixins: [listenOnRootMixin],

    props: {
        duration: {
            type: Number,
            default: 7000,
        },
    },

    data() {
        return {
            isVisible: false,
            timeoutId: 0,
            toolbars: {},
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
            snackbar: state => state.snackbar,
            toolbarVisibility: state => state.page.toolbarVisibility,
        }),

        title() {
            return this.snackbar.title;
        },

        text() {
            return this.snackbar.text;
        },

        closeText() {
            return this.snackbar.closeText || 'DISMISS';
        },

        snackbarStyles() {
            if (this.previewMode) return null;

            let left = '50%';

            if (this.shiftLeft) {
                left = `calc(50% - ${ this.toolbars.editor.width / 2 }px)`;
            }

            if (this.shiftRight) {
                left = `calc(50% + ${ this.toolbars.component.width / 2 }px)`;
            }

            return { left };
        },

        matchingToolbarStates() {
            if (!this.toolbars.component || !this.toolbars.editor) return false;

            return (this.toolbarVisibility.editor && this.toolbarVisibility.component)
                || (!this.toolbarVisibility.editor && !this.toolbarVisibility.component);
        },

        shiftLeft() {
            return this.toolbars.editor
                && this.toolbarVisibility.editor
                && !this.matchingToolbarStates
                && (!this.toolbars.component || this.toolbarVisibility.component === false);
        },

        shiftRight() {
            return this.toolbars.component
                && this.toolbarVisibility.component
                && !this.matchingToolbarStates
                && (!this.toolbars.editor || this.toolbarVisibility.editor === false);
        },
    },

    watch: {
        previewMode(isPreviewMode) {
            if (isPreviewMode) this.isVisible = false;
        },

        snackbar(snackbarData) {
            if (snackbarData.text || (snackbarData.messages && snackbarData.messages.length)) {
                this.isVisible = true;
                window.clearTimeout(this.timeoutId);

                if (!snackbarData.persist) {
                    this.timeoutId = window.setTimeout(() => {
                        this.isVisible = false;
                    }, this.duration);
                }
            } else {
                this.isVisible = false;
            }
        },
    },

    created() {
        this.listenOnRoot('flex::toolbar::update', this.cacheToolbarDimensions);
    },

    methods: {
        cacheToolbarDimensions({ id, dimensions }) {
            if (!this.toolbars[id]) {
                this.$set(this.toolbars, id, {});
            }

            this.toolbars[id] = { ...dimensions };
        },
    },
};
</script>

<style lang="scss">
.snackbar {
    color: $snackbar-color;
    background-color: $snackbar-background-color;
    position: fixed;
    bottom: 15px;
    transform: translateX(-50%);
    padding: 10px 20px;
    border-radius: 4px;
    font-size: 14px;
    border: 1px solid rgba(255, 255, 255, 0.20);
    transition: all 0.45s;
    z-index: 9999;

    &.fade-enter,
    &.fade-leave-to {
        opacity: 0;
    }

    &__content {
        display: inline-block;
    }

    &__title {
        display: inline-block;
        font-weight: 700;
    }

    &__button {
        color: $snackbar-link-color;
        font-weight: 700;
        margin-left: 40px;

        &:hover {
            color: $snackbar-link-hover-color;
        }
    }

    &__messages {
        button {
            &:hover {
                text-decoration: underline;
            }
        }
    }

    &.snackbar--modal {
        .snackbar {
            &__content {
                display: block;
            }
            &__title {
                display: block;
            }
            &__button {
                float: right;
                margin-left: 10px;
                margin-top: 10px;
            }
        }
    }
}
</style>
